/**
 * Brand logo map marker (SAM-5345).
 *
 * Wraps a brand/restaurant logo in the same pin shape used by the built-in
 * task markers (see assets/theme/icons/task-types/PICK_UP.svg), with the logo
 * clipped to the circular area of the pin instead of floating on the map.
 *
 * Once the pickup is done, the same logo pin gets the check badge used by
 * assets/theme/icons/task-types/PICK_UP_COMPLETE.svg, so the restaurant stays
 * recognisable after pickup.
 *
 * The result is an SVG data URI, so it works with any map client's
 * `addIconMarker` / `setIcon`.
 */

const PIN_WIDTH = 35;
const PIN_HEIGHT = 40;
const CIRCLE = { cx: 17.5, cy: 17.6, r: 13.3 };
// Rasterised logo size (px). Plenty for a ~40px marker, incl. retina.
const LOGO_PX = 128;

const CACHE_MAX = 100;
const cache = new Map();

/** Load the logo through a canvas so it can be embedded in the SVG data URI
 * (an <img>-rendered SVG may not load external images). Resolves with a
 * square, centre-cropped PNG data URL, or rejects (e.g. missing CORS). */
function logoToDataUrl(url) {
  return new Promise((resolve, reject) => {
    if (typeof Image === "undefined" || typeof document === "undefined") {
      reject(new Error("No DOM available"));
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) throw new Error("Empty logo image");
        const side = Math.min(w, h);
        const canvas = document.createElement("canvas");
        canvas.width = LOGO_PX;
        canvas.height = LOGO_PX;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, LOGO_PX, LOGO_PX);
        ctx.drawImage(
          img,
          (w - side) / 2,
          (h - side) / 2,
          side,
          side,
          0,
          0,
          LOGO_PX,
          LOGO_PX
        );
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("Failed to load brand logo: " + url));
    img.src = url;
  });
}

const CHECK_BADGE = `<rect x="19" y="1" width="16" height="16" rx="8" fill="#FAFAFA"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M26.9993 1.6665C22.9493 1.6665 19.666 4.94975 19.666 8.99984C19.666 13.0499 22.9493 16.3332 26.9993 16.3332C31.0494 16.3332 34.3327 13.0499 34.3327 8.99984C34.3327 4.94975 31.0494 1.6665 26.9993 1.6665ZM30.4708 6.52843C30.7311 6.78878 30.7311 7.21089 30.4708 7.47124L26.4708 11.4712C26.2104 11.7316 25.7883 11.7316 25.5279 11.4712L23.8613 9.80457C23.6009 9.54423 23.6009 9.12212 23.8613 8.86177C24.1216 8.60142 24.5437 8.60142 24.8041 8.86177L25.9993 10.057L29.5279 6.52843C29.7883 6.26808 30.2104 6.26808 30.4708 6.52843Z" fill="#00BBCB"/>
<rect x="18.5" y="0.5" width="17" height="17" rx="8.5" stroke="#FAFAFA"/>`;

export function buildBrandPinSvg(logoDataUrl, { completed = false } = {}) {
  const { cx, cy, r } = CIRCLE;
  const width = completed ? PIN_WIDTH + 1 : PIN_WIDTH;
  const height = completed ? PIN_HEIGHT + 1 : PIN_HEIGHT;
  const shift = completed ? ' transform="translate(0 1)"' : "";
  const clipId = completed ? "brandLogoClipDone" : "brandLogoClip";
  const svg = `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
<defs><clipPath id="${clipId}"><circle cx="${cx}" cy="${cy}" r="${r}"/></clipPath></defs>
<g${shift}>
<path d="M17.5 1C26.4033 1 34 8.60324 34 17.8516C33.9997 27.0497 26.3115 33.9149 18.793 38.6514C18.3958 38.8804 17.9536 39 17.5 39C17.0506 39 16.6121 38.8831 16.2178 38.6582C8.69712 33.8676 1.00027 27.236 1 17.8516C1 8.42213 8.58986 1 17.5 1Z" fill="#18181B" stroke="#FAFAFA" stroke-width="2"/>
<circle cx="${cx}" cy="${cy}" r="${r}" fill="#FFFFFF"/>
<image xlink:href="${logoDataUrl}" href="${logoDataUrl}" x="${cx - r}" y="${cy -
    r}" width="${r * 2}" height="${r *
    2}" preserveAspectRatio="xMidYMid slice" clip-path="url(#${clipId})"/>
</g>${completed ? "\n" + CHECK_BADGE : ""}
</svg>`;
  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
}

/** Cached logo -> embeddable data URL (null if it can't be embedded). */
function getLogoDataUrl(logoUrl) {
  if (!cache.has(logoUrl)) {
    if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
    cache.set(
      logoUrl,
      logoToDataUrl(logoUrl).catch((e) => {
        cache.delete(logoUrl);
        if (process.env.NODE_ENV !== "production") {
          console.warn("Brand marker fallback:", e.message);
        }
        return null;
      })
    );
  }
  return cache.get(logoUrl);
}

export function getBrandMarkerIcon(logoUrl, { completed = false } = {}) {
  if (typeof logoUrl !== "string" || logoUrl.trim() === "") {
    return Promise.resolve(null);
  }
  return getLogoDataUrl(logoUrl).then((dataUrl) =>
    dataUrl ? buildBrandPinSvg(dataUrl, { completed }) : null
  );
}
